import { wireDateIsValid } from '../shared/wireDate'
import { currentSemesterAnchor, isCurrentSemester } from '../shared/semesters'

export function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr + 'T00:00:00')
  return `${d.getMonth() + 1}月${d.getDate()}日`
}

// Year-qualified variant for lists that can span years — the earlier-feedback
// toggle reveals entries where `7月15日` alone is ambiguous across years.
export function formatDateWithYear(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr + 'T00:00:00')
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}

export function formatMessageDate(dateStr, language) {
  if (!dateStr) return ''
  try {
    return new Intl.DateTimeFormat(language === 'cn' ? 'zh-CN' : 'en-US', {
      month: language === 'cn' ? 'numeric' : 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(dateStr))
  } catch {
    return dateStr
  }
}

// Attended/tracked mirror ATTENDED_STATUSES and ATTENDANCE_STATUSES in
// app/dal/enums.py ({present, makeup} of {present, absent, makeup}). A status
// outside that set is unrecognized, not an absence, so it stays out of the
// denominator rather than depressing a rate on unknown evidence.
const ATTENDED_STATUSES = new Set(['present', 'makeup'])
const TRACKED_STATUSES = new Set(['present', 'absent', 'makeup'])

// CURRENT SEMESTER ONLY (operator report 2026-08-28). `past` is the schedule's
// display history — every past session of every class the student has ever
// been rostered in, capped at 20 rows. Dividing over it made a Fall roster
// that had never missed a Fall session read 73–89%, because prior-semester
// absences sat in the denominator.
//
// Preference order:
//  1. `attendanceBasis` from the normalizer — counted over the UNCAPPED
//     current-semester session set, so a term longer than the display cap
//     still divides by its real session count.
//  2. `past`, scoped to the current semester here. Capped at 20, but never
//     cross-semester. This is the path hand-built fixtures take.
export function summarizeAttendance(scheduleData) {
  const basis = scheduleData?.attendanceBasis
  if (basis && Number.isFinite(basis.tracked) && Number.isFinite(basis.attended)) {
    if (!basis.tracked) return { label: '—' }
    return { label: `${Math.round((basis.attended / basis.tracked) * 100)}%` }
  }

  const pastSessions = scheduleData?.past || []
  const anchor = currentSemesterAnchor(pastSessions)
  const current = pastSessions.filter((session) => isCurrentSemester(session, anchor))
  const tracked = current.filter((session) => TRACKED_STATUSES.has(session.attendanceStatus)).length
  const attended = current.filter((session) => ATTENDED_STATUSES.has(session.attendanceStatus)).length
  if (!tracked) {
    return { label: '—' }
  }
  return {
    label: `${Math.round((attended / tracked) * 100)}%`,
  }
}

export function buildProgressTrend(entries) {
  const recentEntries = entries
    .slice(0, 6)
    .reverse()
    .map((entry) => {
      const values = [
        entry.participation,
        entry.motivation,
        entry.homework,
        entry.metObjective,
      ].filter((value) => Number.isFinite(value))
      if (!values.length) return null
      const average = values.reduce((sum, value) => sum + Number(value), 0) / values.length
      return {
        label: formatDate(entry.sessionDate),
        value: average,
      }
    })
    .filter(Boolean)

  if (recentEntries.length < 2) return null

  const width = 224
  const height = 52
  const minX = 8
  const minY = 8
  const stepX = recentEntries.length === 1 ? 0 : width / (recentEntries.length - 1)
  const points = recentEntries.map((entry, index) => {
    const normalized = Number(entry.value) / 5
    return {
      label: entry.label,
      x: minX + (stepX * index),
      y: minY + ((1 - normalized) * height),
    }
  })

  return {
    points,
    linePath: points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' '),
    areaPath: points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' '),
    firstLabel: points[0].label,
    lastLabel: points[points.length - 1].label,
  }
}


// #596 — which session a homework assignment belongs to in the schedule.
// An assignment created by the close-out flow carries the session it was
// assigned in (`session_id`); it renders ONLY under that session. A legacy
// row without a session is anchored to the LAST past session of its class
// strictly BEFORE its due date (the close-out convention is due = next
// session, so the assigning session is the one before), or the class's most
// recent past session when it has no valid due date. Never to a session of
// another class, never to every session of the class.
//
// Deliberate degradations (the Homework section still lists every
// assignment, so nothing is lost from the portal): a `session_id` that is
// not among the rendered past sessions — an upcoming session, i.e. the
// class closed out TODAY, or one beyond the schedule's 20-row cap — and a
// legacy row whose due date precedes every rendered session both render in
// no schedule row.
export function assignmentsBySession(assignments, pastSessions) {
  const map = new Map()
  const past = Array.isArray(pastSessions) ? pastSessions : []
  const byDateDesc = [...past].sort((a, b) => String(b.sessionDate || '').localeCompare(String(a.sessionDate || '')))
  const rendered = new Set(past.map(s => String(s.id)))
  const sameClass = (s, a) => {
    if (a.class_id) return s.classId === a.class_id
    return Boolean(a.class_name) && a.class_name !== 'Unknown' && s.className === a.class_name
  }
  for (const a of Array.isArray(assignments) ? assignments : []) {
    if (!a || typeof a !== 'object') continue
    let key = null
    if (a.session_id != null && a.session_id !== '') {
      key = String(a.session_id)
    } else {
      const due = wireDateIsValid(a.due_date) ? a.due_date : null
      const anchor = byDateDesc.find(s =>
        sameClass(s, a) && (!due || String(s.sessionDate || '') < due)
      )
      key = anchor ? String(anchor.id) : null
    }
    if (!key || !rendered.has(key)) continue
    if (!map.has(key)) map.set(key, [])
    map.get(key).push(a)
  }
  return map
}

// #597 — nobody uploads homework in this business; completion is the
// feedback `homework` score the teacher records at the session the
// assignment was DUE (the close-out convention: due = next session, and
// every measured due date lands on a session day). The due session IS the
// checking session, so the match is EXACT-DATE: a score from the following
// week's session belongs to the following week's assignment and is never
// attributed backwards. Two assignments of one class due the same day share
// that session's single score — one score per session is the convention.
// Same class: `class_id` first; name only when the assignment has no id and
// the name is not a server fallback.
const CLASS_NAME_FALLBACKS = new Set(['Unknown', 'Unknown Class'])

function sameClassAsAssignment(assignment, classId, className) {
  if (assignment?.class_id) return classId === assignment.class_id
  return Boolean(assignment?.class_name)
    && !CLASS_NAME_FALLBACKS.has(assignment.class_name)
    && className === assignment.class_name
}

// Returns `{score, sessionDate}` when the due session carries a score,
// `{score: null, sessionDate}` when the teacher recorded that session
// WITHOUT a homework score, or null when no feedback row for the due
// session was fetched at all.
export function classroomCheckFor(assignment, feedbackEntries) {
  const due = assignment?.due_date
  if (!wireDateIsValid(due)) return null
  const onDueDay = (Array.isArray(feedbackEntries) ? feedbackEntries : []).filter(e => e && e.sessionDate === due)
  const rows = onDueDay.filter(e => sameClassAsAssignment(assignment, e.classId, e.className))
  if (rows.length) {
    const scored = rows.find(e => e.homework != null)
    if (scored) return { score: scored.homework, sessionDate: due }
    // Not-submitted contract (2026-09): ONLY the teacher's explicit
    // `homeworkSubmitted === false` at the due session is a verdict. null,
    // undefined, or an older wire without the key stays "not recorded".
    if (rows.some(e => e.homeworkSubmitted === false)) return { score: null, sessionDate: due, notSubmitted: true }
    return { score: null, sessionDate: due }
  }
  // A due-day row whose class join missed (classId null + fallback name)
  // cannot be attributed; that is not evidence of absence.
  const unattributable = onDueDay.some(e => !e.classId && CLASS_NAME_FALLBACKS.has(e.className))
  return unattributable ? { score: null, sessionDate: due, unattributable: true } : null
}

// The feedback window the portal actually holds. The progress wire caps
// feedback at 50 rows (newest first, across classes): exactly 50 is
// indistinguishable from a truncated read, so at or before the oldest
// fetched date, absence is UNKNOWN, never "none". A failed progress read
// makes everything unknown. Shared by the schedule and the homework list so
// the two never disagree about what they know.
// Mirrors `LIMIT 50` in netlify/functions/portal-data.ts (progress query);
// a change on either side must be paired.
export const FEEDBACK_WINDOW_LIMIT = 50

export function feedbackWindow(progress) {
  const error = progress?.error || null
  const entries = progress?.data?.entries || progress?.entries || []
  const dates = entries.map(e => e?.sessionDate).filter(d => wireDateIsValid(d))
  const oldestFetchedDate = dates.length ? dates.reduce((a, b) => (b < a ? b : a)) : null
  return {
    error,
    entries,
    mayBeCapped: entries.length >= FEEDBACK_WINDOW_LIMIT,
    oldestFetchedDate,
  }
}

export function feedbackKnownFor(window, date) {
  if (!window || window.error) return false
  if (!wireDateIsValid(date)) return true
  if (window.mayBeCapped && window.oldestFetchedDate && date <= window.oldestFetchedDate) return false
  return true
}

// #596/#597 — the status of an assignment row, in precedence order:
// 1. the upload submission's own status when one exists;
// 2. `checkedInClass` when the teacher scored the homework at the due session;
// 3. `assignmentOpen` while the due date is a valid `YYYY-MM-DD` on/after today;
// 4. past due with no upload: `homeworkUnknown` when the feedback that would
//    carry the check failed to load or lies below the fetched window (absence
//    is not evidence), otherwise `homeworkNotRecorded` — the teacher has not
//    recorded a homework score for that session. A missing or malformed due
//    date reads `homeworkNotRecorded` (it can never read the reassuring Open).
export function assignmentStatus(assignment, today, feedbackEntriesOrWindow = []) {
  const status = assignment?.submission?.status
  if (status && status !== 'not_submitted') return { key: status, score: null }
  const window = Array.isArray(feedbackEntriesOrWindow)
    ? feedbackWindow({ data: { entries: feedbackEntriesOrWindow } })
    : feedbackEntriesOrWindow
  const check = assignment?.classroom_check ?? classroomCheckFor(assignment, window.entries)
  // 2026-09 not-submitted contract: the teacher's explicit verdict at the
  // due session sits right below an upload status and above every #597
  // state (it cannot coexist with a score — the server rejects that pair).
  if (check?.notSubmitted) return { key: 'notSubmitted', score: null }
  if (check && check.score != null) return { key: 'checkedInClass', score: check.score }
  const due = assignment?.due_date
  if (wireDateIsValid(due) && due >= String(today)) return { key: 'assignmentOpen', score: null }
  if (!feedbackKnownFor(window, due) || check?.unattributable) return { key: 'homeworkUnknown', score: null }
  return { key: 'homeworkNotRecorded', score: null }
}

export function assignmentStatusKey(assignment, today, feedbackEntriesOrWindow = []) {
  return assignmentStatus(assignment, today, feedbackEntriesOrWindow).key
}

// The label for a status: the classroom check carries its score.
export function assignmentStatusLabel(status, t) {
  if (status.key === 'checkedInClass' && status.score != null) return `${t('checkedInClass')} · ${status.score}/5`
  return t(status.key)
}

// The CSS modifier for a status key (shared by the schedule row and the
// homework list so the two chips cannot drift).
// Chip modifier. A classroom check is coloured by its score with the SAME
// thresholds as the session detail (≥4 green, 3 orange, else red) so a 1/5
// is never a green "settled" chip.
export function assignmentChipClass(statusKey, score = null) {
  if (statusKey === 'assignmentOpen') return 'open'
  if (statusKey === 'checkedInClass') {
    if (score != null && score >= 4) return 'checked checked-high'
    if (score != null && score >= 3) return 'checked checked-mid'
    return 'checked checked-low'
  }
  if (statusKey === 'homeworkUnknown') return 'unknown'
  if (statusKey === 'homeworkNotRecorded') return 'not-recorded'
  if (statusKey === 'notSubmitted') return 'not-submitted'
  return String(statusKey).replace('_', '-')
}
